<script setup lang="ts">
import type { SiteSectionCategoryId } from "@/data/common/ids";
import type { SectionCategory } from "@/domains/v4b/site-section/site-section.types";

import { normalizeForSearch } from "@voltalis/frontend-utils";
import { BaseDrawer } from "@voltalis/ui-kit/components";
import { PlusIcon } from "@voltalis/ui-kit/icons";
import { computed, ref, watch } from "vue";

import { useToaster } from "@/composables/layout/use-toaster";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EV4bErrorCode } from "@/data/api/kili/common/kili-errors.type";
import { useTypedIntl } from "@/locales/typed-intl";

import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import InputText from "primevue/inputtext";

import SiteSectionCategoryRow from "@/app/tools/new-section-management/site-sections-tree/categories/site-section-category-row.vue";
import CategoryCreationForm from "@/components/tools/site-section/category-creation/category-creation-form.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import ConfirmationDialog from "@/components/ui/dialog/confirmation-dialog.vue";

interface IProps {
  categories: readonly SectionCategory[];
  isLoading?: boolean;
  isCreatingCategory?: boolean;
  isUpdatingCategory?: boolean;
  isDeletingCategory?: boolean;
  createCategory: (name: string) => Promise<boolean>;
  updateCategory: (categoryId: SiteSectionCategoryId, name: string) => Promise<boolean>;
  deleteCategory: (categoryId: SiteSectionCategoryId) => Promise<boolean>;
}

const props = defineProps<IProps>();
const visible = defineModel<boolean>("visible", { required: true });
const emit = defineEmits<{
  (event: "assign-category", categoryId: SiteSectionCategoryId): void;
}>();

const { t, k } = useTypedIntl();
const toast = useToaster();

const searchQuery = ref<string>("");
const isCategoryCreationFormVisible = ref<boolean>(false);
const editedCategoryId = ref<SiteSectionCategoryId>();
const editedCategoryName = ref<string>("");
const categoryPendingDeletion = ref<SectionCategory>();
const isDeleteCategoryConfirmationDialogVisible = ref<boolean>(false);

type DisplayedSectionCategory = SectionCategory & {
  deleteDisabledTooltip?: string;
};

const categoriesWithDeleteDisabledTooltip = computed<DisplayedSectionCategory[]>(() => {
  return props.categories.map((category) => ({
    ...category,
    deleteDisabledTooltip: category.isDeletable
      ? undefined
      : t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.delete.disabledTooltip),
  }));
});

const sortedCategories = computed<DisplayedSectionCategory[]>(() => {
  return [...categoriesWithDeleteDisabledTooltip.value].sort((a, b) => a.name.localeCompare(b.name));
});

const filteredCategories = computed<DisplayedSectionCategory[]>(() => {
  const normalizedSearch = normalizeForSearch(searchQuery.value);
  if (!normalizedSearch) return sortedCategories.value;

  return sortedCategories.value.filter((category) => normalizeForSearch(category.name).includes(normalizedSearch));
});

function resetCreationForm() {
  isCategoryCreationFormVisible.value = false;
}

async function confirmCategoryCreation(categoryName: string) {
  const existingCategory = props.categories.find(
    (category) => normalizeForSearch(category.name) === normalizeForSearch(categoryName),
  );

  if (existingCategory) {
    toast.add({
      severity: "error",
      detail: t(k.error[EV4bErrorCode.SITE_SECTION_CATEGORY_NAME_EXISTS_ON_SITE]),
    });
    return;
  }

  const wasCategoryCreated = await props.createCategory(categoryName);
  if (wasCategoryCreated) {
    resetCreationForm();
  }
}

function startCategoryEdition(category: SectionCategory) {
  editedCategoryId.value = category.id;
  editedCategoryName.value = category.name;
}

function resetCategoryEdition() {
  editedCategoryId.value = undefined;
  editedCategoryName.value = "";
}

async function confirmCategoryEdition(category: SectionCategory) {
  const categoryName = editedCategoryName.value.trim();
  if (!categoryName) return;

  if (normalizeForSearch(categoryName) === normalizeForSearch(category.name)) {
    resetCategoryEdition();
    return;
  }

  const existingCategory = props.categories.find(
    (existingCategory) =>
      existingCategory.id !== category.id &&
      normalizeForSearch(existingCategory.name) === normalizeForSearch(categoryName),
  );

  if (existingCategory) {
    toast.add({
      severity: "error",
      detail: t(k.error[EV4bErrorCode.SITE_SECTION_CATEGORY_NAME_EXISTS_ON_SITE]),
    });
    return;
  }

  const wasCategoryUpdated = await props.updateCategory(category.id, categoryName);
  if (wasCategoryUpdated) {
    resetCategoryEdition();
  }
}

function openDeleteConfirmation(category: SectionCategory) {
  if (!category.isDeletable) return;

  categoryPendingDeletion.value = category;
  isDeleteCategoryConfirmationDialogVisible.value = true;
}

function assignCategory(category: SectionCategory) {
  emit("assign-category", category.id);
}

async function confirmCategoryDeletion() {
  const category = categoryPendingDeletion.value;
  if (!category) return;

  const wasCategoryDeleted = await props.deleteCategory(category.id);
  if (wasCategoryDeleted) {
    isDeleteCategoryConfirmationDialogVisible.value = false;
    categoryPendingDeletion.value = undefined;
  }
}

watch(visible, (isVisible) => {
  if (!isVisible) {
    searchQuery.value = "";
    resetCreationForm();
    resetCategoryEdition();
    categoryPendingDeletion.value = undefined;
    isDeleteCategoryConfirmationDialogVisible.value = false;
  }
});
</script>

<template>
  <BaseDrawer
    v-model="visible"
    class="site-section-category-drawer"
    :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.component"
  >
    <template #title>
      <h4 class="text-lg font-semibold text-text-primary">
        {{ t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.title) }}
      </h4>
    </template>

    <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-6">
      <IconField iconPosition="left">
        <InputIcon class="pi pi-search" />
        <InputText
          v-model="searchQuery"
          class="h-[52px] w-full !rounded-lg !border-neutral-light !pl-11 !text-base"
          :placeholder="t(k.tools.siteManagement.siteTreeStructure.searchCategory)"
          :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.searchInput"
        />
      </IconField>

      <div class="flex flex-col gap-[18px]">
        <SiteSectionCategoryRow
          v-for="category in filteredCategories"
          :key="category.id"
          v-model:edited-category-name="editedCategoryName"
          :category="category"
          :is-editing="editedCategoryId === category.id"
          :is-updating-category="props.isUpdatingCategory"
          :is-deleting-category="props.isDeletingCategory"
          @assign-category="assignCategory"
          @start-edition="startCategoryEdition"
          @reset-edition="resetCategoryEdition"
          @confirm-edition="confirmCategoryEdition"
          @open-delete-confirmation="openDeleteConfirmation"
        />

        <div
          v-if="!filteredCategories.length"
          class="py-4 text-sm text-text-secondary"
        >
          {{ t(k.global.misc.noResult) }}
        </div>

        <div class="py-3">
          <template v-if="isCategoryCreationFormVisible">
            <CategoryCreationForm
              :is-loading="props.isCreatingCategory"
              @confirm="confirmCategoryCreation"
              @cancel="resetCreationForm"
            />
          </template>

          <HospitalityButton
            v-else
            :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.createButton"
            :icon="PlusIcon"
            severity="link"
            size="sm"
            @click="isCategoryCreationFormVisible = true"
          >
            {{ t(k.tools.siteManagement.siteTreeStructure.createCategory) }}
          </HospitalityButton>
        </div>
      </div>
    </div>
  </BaseDrawer>

  <ConfirmationDialog
    v-model="isDeleteCategoryConfirmationDialogVisible"
    delete-mode
    :callback="confirmCategoryDeletion"
    :title="
      t(
        k.tools.siteManagement.siteTreeStructure.categoryDrawer.delete.title({
          categoryName: categoryPendingDeletion?.name ?? '',
        }),
      )
    "
    :subtitle="t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.delete.message)"
    :submit-text="t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.delete.submit)"
  />
</template>
